import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/** Friendly copy for the `?github_error=` codes the GitHub App install callback redirects with (see src/routes/setup.ts). */
export const GITHUB_INSTALL_ERROR_MESSAGES: Record<string, string> = {
  registration_failed: 'Failed to register GitHub App installation. Please try again.',
  missing_installation: "GitHub didn't say which installation to connect. Please install the app again from this page.",
  invalid_state: 'The GitHub install link expired or was already used. Please start again from settings.',
  insufficient_permissions: 'Only workspace admins can connect the GitHub App.',
  not_a_member:
    "You're not a member of the workspace this GitHub App was installed for. Ask an admin for access, or switch to the right workspace and try again.",
  missing_workspace:
    'Missing workspace from the GitHub redirect. Please start the installation again from your workspace settings.',
  authorization_denied: 'GitHub sign-in was cancelled, so nothing was connected.',
  authorization_failed: "Couldn't confirm your GitHub access. Please try again.",
  installation_not_accessible: "Your GitHub account can't access that installation, so it wasn't connected.",
  no_new_installation:
    "No new GitHub installation to connect. Either your GitHub org owner hasn't approved the app yet, or your GitHub account can't see any repository it was installed on.",
};

/**
 * Surfaces the GitHub App install outcome the setup callback lands on — `?github_success=true`,
 * `?github_error=<code>` or `?github_pending=requested` (a non-owner's install became a request to the
 * org owners) — as a toast, then strips the query so a refresh doesn't re-fire it. The callback lands
 * directly on /settings/context (the GitHub section), preserving the query. `?github_choose=` is not a
 * toast: `ScmSection` reads it and opens the installation chooser.
 */
export function useGithubInstallResultToast(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const shownFor = useRef<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('github_success');
    const error = searchParams.get('github_error');
    const pending = searchParams.get('github_pending');
    const key = success ? `success:${success}` : error ? `error:${error}` : pending ? `pending:${pending}` : null;
    if (!key || shownFor.current === key) return;
    shownFor.current = key;

    if (success) {
      toast.success('GitHub App installed successfully!');
    } else if (error) {
      toast.error(GITHUB_INSTALL_ERROR_MESSAGES[error] ?? 'GitHub App installation failed. Please try again.');
    } else if (pending === 'requested') {
      toast.success('Request sent to your GitHub org owners. Once one approves, click Check approval.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('github_success');
    next.delete('github_error');
    next.delete('github_pending');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
}
